import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const SERVICES_MENU = [
  { label: 'Autohjælp 24/7', path: '/autohjaelp', badge: '24/7' },
  { label: 'Autotransport', path: '/ydelser/autotransport-ydelser' },
  { label: 'Bjærgning', path: '/bjaergning' },
  { label: 'Bugsering af bil', path: '/bugsering-af-bil' },
  { label: 'Bugsering af lastbil', path: '/bugsering-af-lastbil' },
  { label: 'Bilopbevaring', path: '/bilopbevaring', badge: 'Sikret' },
  { label: 'Dækservice (fra 1/11-2026)', path: '/daekcenter', badge: 'Kommende' },
  { label: 'Dækcenter lastbil', path: '/daekcenter-lastbil' },
  { label: 'Dækskift lastbil', path: '/daekskift-lastbil' },
  { label: 'Fritrækning', path: '/fritraekning' },
  { label: 'Krantransport', path: '/ydelser/krantransport' },
  { label: 'Maskintransport', path: '/ydelser/maskintransport' },
  { label: 'Transport på sengetrailer', path: '/ydelser/transport-paa-sengetrailer' },
  { label: 'Kørsel med gardintrailer', path: '/ydelser/gardintrailer-medbringertruck' },
  { label: 'Servicevogn', path: '/ydelser/servicevogn' },
  { label: 'Godstransport', path: '/ydelser/godstransport' },
  { label: 'Skiltevogn', path: '/skiltevogn' },
  { label: 'TMA køretøj', path: '/TMA' },
  { label: 'Tow Truck / International', path: '/tow-truck' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Track scroll position to shrink header when sticky/fixed
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const y = window.scrollY;
          setIsScrolled((prev) => {
            // Hysteresis prevents oscillation caused by header height changes
            if (!prev && y > 80) return true;
            if (prev && y < 20) return false;
            return prev;
          });
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on page change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname]);

  // Click outside to close desktop dropdown
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isServicesActive = location.pathname.startsWith('/ydelser');

  return (
    <header className="sticky top-0 z-40 bg-white shadow-sm border-b border-zinc-200 [overflow-anchor:none] w-full max-w-full">
      {/* 1. TOP UTILITY BAR (Reference inspired) */}
      <div className="bg-zinc-950 text-zinc-300 text-xs border-b border-zinc-800 w-full overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-9 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 min-w-0">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-zinc-400">
              <i className="fa-solid fa-location-dot text-red-500" aria-hidden="true" />
              <span>Ugerløse</span>
            </span>
            <span className="hidden md:inline-flex items-center gap-1.5 text-zinc-400">
              <span className="text-zinc-700">|</span>
              <i className="fa-solid fa-location-dot text-red-500" aria-hidden="true" />
              <span>Odense</span>
            </span>
            <span className="hidden lg:inline-flex items-center gap-1.5 text-zinc-400">
              <span className="text-zinc-700">|</span>
              <i className="fa-solid fa-location-dot text-red-500" aria-hidden="true" />
              <span>Hedensted</span>
            </span>
            <span className="hidden xs:inline-flex items-center gap-1.5 text-red-400 font-semibold tracking-wide truncate">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
              <span className="truncate">Døgnbemandet Vagtcentral 24/7/365</span>
            </span>
          </div>

          <div className="flex items-center gap-3 sm:gap-6 text-xs shrink-0 font-medium ml-auto">
            <a
              href="mailto:kontor@mcautotransport.dk"
              className="hidden md:inline-flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <i className="fa-solid fa-envelope text-zinc-400" aria-hidden="true" />
              <span className="whitespace-nowrap">kontor@mcautotransport.dk</span>
            </a>
            <a
              href="tel:+4570700800"
              className="inline-flex items-center gap-1.5 text-white hover:text-red-400 transition-colors font-bold whitespace-nowrap text-xs"
            >
              <i className="fa-solid fa-phone text-red-500" aria-hidden="true" />
              <span>Vagtcentral: +45 70 700 800</span>
            </a>
            <a
              href="https://www.facebook.com/MCautotransport/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Besøg os på Facebook"
              className="hover:text-blue-400 transition-colors px-1"
            >
              <i className="fa-brands fa-facebook-f text-sm" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>

      {/* 2. MAIN NAVIGATION */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col">
          {/* Logo row: Centered on desktop, space-between on mobile */}
          <div
            className={`flex items-center justify-between transition-all duration-300 min-[1200px]:justify-center min-[1200px]:relative ${
              isScrolled
                ? 'h-14 min-[1200px]:h-auto min-[1200px]:py-2'
                : 'h-20 min-[1200px]:h-auto min-[1200px]:py-3.5'
            }`}
          >
            {/* Professionally integrated company name lockup */}
            <Link
              to="/"
              className="flex items-center gap-2.5 sm:gap-3 group focus:outline-none shrink select-none"
              aria-label="MC Transport - Tilbage til forsiden"
            >
              <div
                className={`bg-gradient-to-br from-red-600 to-red-700 text-white rounded-lg flex items-center justify-center font-condensed font-black shadow-md border border-red-500 transition-all duration-300 group-hover:scale-105 shrink-0 ${
                  isScrolled ? 'w-8 h-8 sm:w-9 sm:h-9 text-base sm:text-lg' : 'w-10 h-10 sm:w-12 sm:h-12 text-lg sm:text-2xl'
                }`}
              >
                <span>MC</span>
              </div>
              <div className="flex flex-col text-left">
                <div className="flex items-baseline leading-none">
                  <span
                    className={`font-condensed font-extrabold uppercase tracking-tight text-zinc-950 group-hover:text-red-600 transition-colors ${
                      isScrolled ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'
                    }`}
                  >
                    MC <span className="text-red-600">TRANSPORT</span>
                  </span>
                </div>
                <span
                  className={`font-condensed font-bold uppercase text-zinc-500 tracking-[0.18em] leading-none transition-all duration-300 ${
                    isScrolled ? 'text-[9px] mt-0.5 hidden sm:block' : 'text-[10px] sm:text-[11px] mt-1'
                  }`}
                >
                  Autotransport · Bjærgning · Vagtcentral 24/7
                </span>
              </div>
            </Link>

            {/* Desktop Right Phone CTA button */}
            <div className="hidden min-[1200px]:flex items-center gap-3 absolute right-0 top-1/2 -translate-y-1/2">
              <a
                href="tel:+4570700800"
                className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-condensed text-lg font-bold uppercase tracking-wider rounded shadow transition-all hover:shadow-md hover:-translate-y-0.5 whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>+45 70 700 800</span>
              </a>
            </div>

            {/* Mobile Right Controls (< 1200px) */}
            <div className="flex min-[1200px]:hidden items-center gap-1.5 sm:gap-2 shrink-0">
              <a
                href="tel:+4570700800"
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white font-condensed text-sm font-bold uppercase rounded shadow whitespace-nowrap"
                aria-label="Ring til vagtcentral +45 70 700 800"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>24/7:</span>
                <span>+45 70 700 800</span>
              </a>

              <button
                type="button"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                className="p-2.5 rounded-md text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 focus:outline-none"
                aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? (
                  <i className="fa-solid fa-xmark text-2xl" aria-hidden="true" />
                ) : (
                  <i className="fa-solid fa-bars text-2xl" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* Desktop Nav - visible ONLY >= 1200px, centered under logo */}
          <nav
            aria-label="Hovedmenu"
            className={`hidden min-[1200px]:flex items-center justify-center gap-1.5 xl:gap-2 text-sm font-semibold text-zinc-800 border-t border-zinc-100 w-full transition-all duration-300 ${
              isScrolled ? 'py-1.5' : 'py-2.5'
            }`}
          >
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-3 py-2 rounded transition-colors ${
                  isActive ? 'text-red-600 font-bold bg-red-50' : 'hover:text-red-600 hover:bg-zinc-50'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Dropdown for Ydelser */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setServicesDropdownOpen((prev) => !prev)}
                onMouseEnter={() => setServicesDropdownOpen(true)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded transition-colors ${
                  isServicesActive ? 'text-red-600 font-bold bg-red-50' : 'hover:text-red-600 hover:bg-zinc-50'
                }`}
                aria-expanded={servicesDropdownOpen}
              >
                <span>Ydelser</span>
                <i
                  className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                    servicesDropdownOpen ? 'rotate-180 text-red-600' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>

              {servicesDropdownOpen && (
                <div
                  onMouseLeave={() => setServicesDropdownOpen(false)}
                  className="absolute left-0 mt-1 w-72 rounded-lg bg-white shadow-xl border border-zinc-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                >
                  <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                    Vores kompetencer
                  </div>
                  <div className="max-h-[70vh] overflow-y-auto py-1">
                    {SERVICES_MENU.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) =>
                          `flex items-center justify-between px-3.5 py-2 text-xs transition-colors ${
                            isActive
                              ? 'bg-red-50 text-red-600 font-bold'
                              : 'text-zinc-700 hover:bg-zinc-100 hover:text-red-600'
                          }`
                        }
                      >
                        <span className="truncate">{item.label}</span>
                        {item.badge && (
                          <span
                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 ${
                              item.badge === '24/7'
                                ? 'bg-red-600 text-white'
                                : 'bg-zinc-800 text-white'
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                  <div className="p-2 border-t border-zinc-100 bg-zinc-50 rounded-b-lg">
                    <Link
                      to="/ydelser"
                      className="block text-center text-xs font-bold text-red-600 hover:underline py-1"
                    >
                      Se alle ydelser i oversigten →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/daekcenter"
              className={({ isActive }) =>
                `px-2.5 xl:px-3 py-2 rounded transition-colors ${
                  isActive ? 'text-red-600 font-bold bg-red-50' : 'hover:text-red-600 hover:bg-zinc-50'
                }`
              }
            >
              Dækservice
            </NavLink>

            <NavLink
              to="/bilopbevaring"
              className={({ isActive }) =>
                `px-2.5 xl:px-3 py-2 rounded transition-colors ${
                  isActive ? 'text-red-600 font-bold bg-red-50' : 'hover:text-red-600 hover:bg-zinc-50'
                }`
              }
            >
              Bilopbevaring
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-2.5 xl:px-3 py-2 rounded transition-colors ${
                  isActive ? 'text-red-600 font-bold bg-red-50' : 'hover:text-red-600 hover:bg-zinc-50'
                }`
              }
            >
              Om MC Transport
            </NavLink>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `px-2.5 xl:px-3 py-2 rounded transition-colors ${
                  isActive ? 'text-red-600 font-bold bg-red-50' : 'hover:text-red-600 hover:bg-zinc-50'
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/chauffoer-soeges"
              className={({ isActive }) =>
                `px-2.5 xl:px-3 py-2 rounded transition-colors ${
                  isActive ? 'text-red-600 font-bold bg-red-50' : 'hover:text-red-600 hover:bg-zinc-50'
                }`
              }
            >
              Ledige job
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-3 py-2 rounded transition-colors ${
                  isActive ? 'text-red-600 font-bold bg-red-50' : 'hover:text-red-600 hover:bg-zinc-50'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>
        </div>
      </div>

      {/* 3. MOBILE NAVIGATION DRAWER (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-zinc-900 text-white border-t border-zinc-800 animate-in fade-in duration-200 max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain shadow-2xl">
          {/* Emergency call card inside mobile menu */}
          <div className="p-4 bg-zinc-950/80 border-b border-zinc-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
              <span className="text-xs uppercase font-bold tracking-wider text-red-500 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Vagtcentral døgnvagt 24/7
              </span>
              <span className="text-[11px] text-zinc-400">Akut hjælp i hele DK</span>
            </div>
            <a
              href="tel:+4570700800"
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-condensed text-lg font-bold uppercase rounded shadow-lg"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>RING +45 70 700 800</span>
            </a>
          </div>

          <nav aria-label="Mobil menu" className="px-4 py-3 divide-y divide-zinc-800 text-sm">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `block py-3 font-semibold transition-colors ${
                  isActive ? 'text-red-500' : 'text-zinc-200 hover:text-white'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Mobile Ydelser accordion */}
            <div>
              <button
                type="button"
                onClick={() => setMobileServicesOpen((prev) => !prev)}
                className="w-full flex items-center justify-between py-3 font-semibold text-zinc-200 hover:text-white"
              >
                <span>Ydelser</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform ${
                    mobileServicesOpen ? 'rotate-180 text-red-500' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>

              {mobileServicesOpen && (
                <div className="pl-3 pb-3 space-y-2 border-l border-zinc-800 ml-1">
                  <Link
                    to="/ydelser"
                    className="block py-1 text-xs font-bold text-red-400 hover:underline"
                  >
                    → Se oversigt over alle ydelser
                  </Link>
                  {SERVICES_MENU.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center justify-between py-1 text-xs transition-colors ${
                          isActive ? 'text-red-500 font-bold' : 'text-zinc-400 hover:text-white'
                        }`
                      }
                    >
                      <span>{item.label}</span>
                      {item.badge && (
                        <span
                          className={`text-[9px] font-bold px-1.5 py-0.2 rounded uppercase ${
                            item.badge === '24/7' ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300'
                          }`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/daekcenter"
              className={({ isActive }) =>
                `block py-3 font-semibold transition-colors ${
                  isActive ? 'text-red-500' : 'text-zinc-200 hover:text-white'
                }`
              }
            >
              Dækservice (fra 1/11-2026 i Hedensted)
            </NavLink>

            <NavLink
              to="/bilopbevaring"
              className={({ isActive }) =>
                `block py-3 font-semibold transition-colors ${
                  isActive ? 'text-red-500' : 'text-zinc-200 hover:text-white'
                }`
              }
            >
              Bilopbevaring
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `block py-3 font-semibold transition-colors ${
                  isActive ? 'text-red-500' : 'text-zinc-200 hover:text-white'
                }`
              }
            >
              Om MC Transport
            </NavLink>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `block py-3 font-semibold transition-colors ${
                  isActive ? 'text-red-500' : 'text-zinc-200 hover:text-white'
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/chauffoer-soeges"
              className={({ isActive }) =>
                `block py-3 font-semibold transition-colors ${
                  isActive ? 'text-red-500' : 'text-zinc-200 hover:text-white'
                }`
              }
            >
              Ledige job
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `block py-3 font-semibold transition-colors ${
                  isActive ? 'text-red-500' : 'text-zinc-200 hover:text-white'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Mobile footer contact info */}
          <div className="p-4 bg-zinc-950 text-xs text-zinc-400 space-y-2 border-t border-zinc-800">
            <div>
              <span className="font-semibold text-white">E-mail: </span>
              <a href="mailto:kontor@mcautotransport.dk" className="hover:text-red-400">
                kontor@mcautotransport.dk
              </a>
            </div>
            <div>
              <span className="font-semibold text-white">Adresser: </span>
              Annexgårdsvej 7, 4350 Ugerløse & Lundagervej 4, Hedensted
            </div>
            <div className="pt-2 flex items-center gap-3">
              <a
                href="https://www.facebook.com/MCautotransport/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white hover:text-blue-400"
              >
                <i className="fa-brands fa-facebook text-base" aria-hidden="true" />
                <span>Følg os på Facebook</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
